/**
 * Validation for service requests / work orders, branches and departments.
 *
 * READ CLAUDE.md SECTION 4 FIRST. There is deliberately no schema in this file
 * for a test input, a reading, a unit of measurement, a formula, a rounding
 * rule, an acceptance limit, a chart or an Excel mapping — for any test,
 * including the five initial ones. A requested service is validated as a
 * COMMERCIAL line: which catalogue entry it names, how many, at what price and
 * where that price came from.
 *
 * THREE RULES BELOW ARE ACCEPTANCE CRITERIA, NOT PREFERENCES:
 *
 *  1. A DUE DATE IS OPTIONAL AND NEVER DEFAULTED. Leaving it out means the
 *     laboratory promised no date. Filling it in with today's, or with the
 *     request date, would invent a commitment nobody made (CLAUDE.md
 *     section 8: an empty field is never zero).
 *
 *  2. CANCELLING REQUIRES A REASON. Not optionally, not with a default of
 *     "cancelled by user" — a stated reason, because a cancellation nobody
 *     explained is one nobody can account for afterwards.
 *
 *  3. CHANGING THE SCOPE AFTER WORK HAS STARTED REQUIRES ACKNOWLEDGING THREE
 *     NAMED IMPACTS, each affirmed separately. One "I understand" box would be
 *     ticked without reading; three named consequences are what the person is
 *     actually agreeing to.
 *
 * The backend re-validates every one of these. The frontend uses them so the
 * user hears about a problem before the round trip.
 */
import { z } from 'zod';
import { ALL_WORK_ORDER_PRIORITIES, ALL_WORK_ORDER_SERVICE_STATES, } from '@lims/shared-types';
import { bilingualTextSchema, businessTextSchema, optionalBusinessText, isoDateSchema, listQuerySchema, moneyMinorSchema, reasonSchema, uuidSchema, } from './primitives.js';
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    // An empty string is an absent value, not a value of "". Storing "" would
    // make an empty field look like a recorded fact.
    .transform((value) => (value === '' ? null : value));
export const workOrderPrioritySchema = z.enum(ALL_WORK_ORDER_PRIORITIES);
export const workOrderServiceStateSchema = z.enum(ALL_WORK_ORDER_SERVICE_STATES);
/**
 * A discount is a percentage OR an amount, never both.
 *
 * The same rule, and the same reasoning, as a quotation line (decision D43):
 * percentage-and-amount together is not a richer discount, it is an ambiguous
 * one, and nobody could say afterwards which was applied first.
 */
const discountShape = {
    discountPercent: z.number().min(0).max(100).nullable().optional(),
    discountAmountMinor: moneyMinorSchema.nonnegative().nullable().optional(),
};
function discountIsUnambiguous(value) {
    return !(value.discountPercent !== null &&
        value.discountPercent !== undefined &&
        value.discountAmountMinor !== null &&
        value.discountAmountMinor !== undefined);
}
// Not `as const`: zod's `refine` takes a mutable `path`, and a readonly tuple
// is rejected by the overload.
const DISCOUNT_REFINEMENT = {
    message: 'A discount is a percentage or an amount, never both',
    path: ['discountAmountMinor'],
};
// ---------------------------------------------------------------------------
// Branches
// ---------------------------------------------------------------------------
/**
 * A laboratory's own branch.
 *
 * NOTHING HERE IS SHIPPED WITH THE PRODUCT. A branch name, code, address and
 * telephone number belong to one laboratory, so they are rows it creates —
 * never a list written into the source, and not even a seeded "Main Branch",
 * which would be this product asserting an identity that belongs to a customer
 * (CLAUDE.md section 6.1, decision D53).
 */
const branchCoreSchema = z.object({
    code: optionalText(32),
    name: businessTextSchema,
    addressLine: optionalBusinessText(500),
    contactEmail: z
        .string()
        .trim()
        .toLowerCase()
        .email()
        .max(320)
        .nullable()
        .optional(),
    contactPhone: z.string().trim().min(5).max(32).nullable().optional(),
    notes: optionalText(2000),
});
export const createBranchSchema = branchCoreSchema;
export const updateBranchSchema = branchCoreSchema.partial().extend({
    /** Branches are deactivated, never deleted: work was carried out at one. */
    isActive: z.boolean().optional(),
    reason: reasonSchema.optional(),
});
export const listBranchesQuerySchema = z.object({
    includeInactive: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
});
// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------
/**
 * A department inside the laboratory.
 *
 * Like a branch, entirely the laboratory's own. `branchId` is optional because
 * a single-site laboratory has departments and no meaningful branch to hang
 * them from, and demanding one would mean inventing a branch.
 */
const departmentCoreSchema = z.object({
    code: optionalText(32),
    name: businessTextSchema,
    description: optionalBusinessText(2000),
    branchId: uuidSchema.nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
export const createDepartmentSchema = departmentCoreSchema;
export const updateDepartmentSchema = departmentCoreSchema.partial().extend({
    /** Deactivated, never deleted: a catalogue entry and a work order cite it. */
    isActive: z.boolean().optional(),
    reason: reasonSchema.optional(),
});
export const listDepartmentsQuerySchema = z.object({
    branchId: uuidSchema.optional(),
    includeInactive: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
});
// ---------------------------------------------------------------------------
// Requested services
// ---------------------------------------------------------------------------
/**
 * One requested service.
 *
 * `unitPriceMinor` is OPTIONAL on input, exactly as on a quotation line. Left
 * out, the server resolves the price itself — the client's agreement first,
 * then the catalogue — and records where it came from. Supplied and different
 * from the resolved price, it is an OVERRIDE: it requires `pricing.override`
 * and a reason, and the API refuses it without either.
 *
 * WHEN THE WORK ORDER COMES FROM AN ACCEPTED QUOTATION, NONE OF THIS RUNS.
 * The prices on the quotation are what the client agreed to, and they are
 * carried across as snapshots rather than resolved again — re-reading the
 * catalogue would let a reprice between acceptance and the job silently change
 * what the client is charged.
 */
const workOrderServiceShape = {
    catalogueItemId: uuidSchema.nullable().optional(),
    /**
     * Required when there is no catalogue entry — mobilisation, a site visit
     * or a call-out is a legitimate line with nothing in the catalogue behind
     * it, and it still has to say what it is.
     */
    description: bilingualTextSchema.optional(),
    departmentId: uuidSchema.nullable().optional(),
    quantity: z
        .number()
        .positive()
        .max(1_000_000)
        // Three decimals is enough for half a day or a quarter of an hour, and
        // stops a float with fifteen digits of noise reaching the arithmetic.
        .refine((value) => Number.isInteger(value * 1000), {
        message: 'A quantity may carry at most three decimal places',
    }),
    unitPriceMinor: moneyMinorSchema.optional(),
    priceOverrideReason: reasonSchema.optional(),
    ...discountShape,
    notes: optionalText(1000),
};
const NAMES_ITSELF = {
    message: 'A requested service names a catalogue entry or describes itself',
    path: ['description'],
};
const namesItself = (line) => Boolean(line.catalogueItemId) || Boolean(line.description);
export const workOrderServiceSchema = z
    .object(workOrderServiceShape)
    .refine(namesItself, NAMES_ITSELF)
    .refine(discountIsUnambiguous, DISCOUNT_REFINEMENT);
/**
 * One line of `PUT /work-orders/{id}/services` (UX follow-up, D195).
 *
 * `id` names an EXISTING service of this work order, which is then changed IN
 * PLACE — it keeps its identity, its line number and the price it was agreed
 * at, and anything already linked to it (a test assignment, an invoice line)
 * stays linked. A line without `id` is a new service. An existing service left
 * out of the list is removed (requested) or cancelled (started) — and refused
 * where work or billing depends on it. A client that sends no ids gets the
 * original whole-list behaviour.
 *
 * `unitPriceMinor` on an existing line: left out, the stored price stands; the
 * same as stored, nothing changes; different, it is an override (permission
 * and reason) unless it is the line's own agreed or catalogue price.
 */
export const workOrderServiceLineSchema = z
    .object({ id: uuidSchema.optional(), ...workOrderServiceShape })
    .refine(namesItself, NAMES_ITSELF)
    .refine(discountIsUnambiguous, DISCOUNT_REFINEMENT);
/**
 * The acknowledgement a scope change needs once work has started.
 *
 * All three are `z.literal(true)`, not booleans. A schema that accepted
 * `false` would let the interface send the field and satisfy the API while
 * acknowledging nothing, which is precisely the box-ticking the criterion
 * exists to prevent.
 */
export const scopeChangeAcknowledgementSchema = z.object({
    price: z.literal(true),
    deadline: z.literal(true),
    workInProgress: z.literal(true),
    note: optionalText(2000),
});
// ---------------------------------------------------------------------------
// Work orders
// ---------------------------------------------------------------------------
const workOrderCoreSchema = z.object({
    projectId: uuidSchema.nullable().optional(),
    locationId: uuidSchema.nullable().optional(),
    branchId: uuidSchema.nullable().optional(),
    /** Who asked for the work, where that is not simply the client. */
    requestingPartyName: optionalText(200),
    requestingPartyContactId: uuidSchema.nullable().optional(),
    requestingPartyEmail: z
        .string()
        .trim()
        .toLowerCase()
        .email()
        .max(320)
        .nullable()
        .optional(),
    requestingPartyPhone: z.string().trim().min(5).max(32).nullable().optional(),
    /** The client's own purchase order or project number, as they quote it. */
    purchaseOrderNumber: optionalText(100),
    clientReference: optionalText(100),
    requestDate: isoDateSchema.optional(),
    /**
     * OPTIONAL, AND NEVER DEFAULTED. Absent means the laboratory promised no
     * date. Defaulting it would put a commitment on the record that nobody made,
     * and the list would then report the job late against it.
     */
    dueDate: isoDateSchema.nullable().optional(),
    priority: workOrderPrioritySchema.optional(),
    subject: optionalBusinessText(500),
    notes: optionalText(4000),
});
export const createWorkOrderSchema = workOrderCoreSchema
    .extend({
    /** Required. Work is carried out for a client who is on file. */
    clientId: uuidSchema,
    /**
     * Raise this request from an accepted quotation.
     *
     * The services and their prices are then taken from the quotation as
     * SNAPSHOTS and `services` must not also be supplied: two sources for one
     * list is how the client's agreed price and the job's price come to
     * differ.
     */
    quotationId: uuidSchema.optional(),
    pricingAgreementId: uuidSchema.optional(),
    services: z.array(workOrderServiceSchema).max(200).optional(),
})
    .refine((value) => !(value.quotationId && value.services), {
    message: 'A request raised from a quotation takes its services from that quotation',
    path: ['services'],
});
export const updateWorkOrderSchema = workOrderCoreSchema.partial().extend({
    reason: reasonSchema.optional(),
});
/**
 * Replace the requested services.
 *
 * A `PUT`, because it is a set operation: the scope of a job is renegotiated
 * as a whole. Once any work has started the acknowledgement is REQUIRED, and
 * the API refuses the change without it.
 */
export const setWorkOrderServicesSchema = z.object({
    services: z
        .array(workOrderServiceLineSchema)
        .max(200)
        .refine((lines) => {
        const ids = lines.map((line) => line.id).filter(Boolean);
        return new Set(ids).size === ids.length;
    }, { message: 'A service may appear only once in the list' }),
    impactAcknowledgement: scopeChangeAcknowledgementSchema.optional(),
    reason: reasonSchema.optional(),
});
/**
 * Record where one requested service has got to.
 *
 * A PERSON states this. Nothing in Phase 6 infers it, and marking one service
 * complete never marks the request complete — the request's status is derived
 * from every service, which is what makes "partial is not complete" structural
 * rather than remembered.
 */
export const updateWorkOrderServiceStateSchema = z.object({
    state: workOrderServiceStateSchema,
    note: optionalText(2000),
    /** Required when cancelling one service, for the same reason as cancelling
     *  the whole request: an unexplained stop is one nobody can account for. */
    reason: reasonSchema.optional(),
});
/**
 * Cancel a request.
 *
 * THE REASON IS REQUIRED, and there is no `deleteRecords` flag of any kind.
 * Cancellation stops unexecuted work; it removes nothing, keeps the Job
 * Number, keeps the dates, and leaves every service that was already complete
 * exactly as it was.
 */
export const cancelWorkOrderSchema = z.object({
    reason: reasonSchema,
    cancelledAt: z.string().datetime({ offset: true }).optional(),
});
export const listWorkOrdersQuerySchema = listQuerySchema.extend({
    clientId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    quotationId: uuidSchema.optional(),
    priority: workOrderPrioritySchema.optional(),
    /**
     * Filter on the DERIVED status. It is not a stored column, so the backend
     * turns each of these into a predicate over the services themselves.
     */
    status: z
        .enum([
        'awaiting_sample',
        'in_progress',
        'partially_complete',
        'complete',
        'cancelled',
    ])
        .optional(),
    /** Also derived: past the promised date, and not finished or cancelled. */
    overdue: z.enum(['true', 'false']).optional(),
});
//# sourceMappingURL=work-orders.js.map